import { useForm, useWatch } from "react-hook-form";
import Modal from "@via/components/modal";
import SelectComponent from "@via/components/select/select";
import { SelectOption } from "@via/utils/interfaces";
import { type JSX, useEffect, useMemo, useState } from "react";
import {
  FileQueryFilter,
  FilterLogic,
} from "@via/pages/sample-trackers/samples/types/filters.ts";
import {
  useReportFeatures,
  useReportPaths,
} from "@via/pages/sample-trackers/samples/sample-explorer.api.tsx";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import FileQueryFilterCondition from "@via/pages/sample-trackers/samples/components/file-query-filter-condition.tsx";
import { ModalSize } from "@via/utils/constants.tsx";

interface AddFileQueryFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddFilter: (filter: FileQueryFilter) => void;
  projectId: string;
  editingFilter?: FileQueryFilter;
}

interface FilterFormData {
  reportRelativePath: string;
  selectedFields: string[];
}

interface FieldAggregation {
  fieldName: string;
  aggregation: "max" | "avg" | "sum" | "min";
}

interface FilterCondition {
  field: string;
  operator: string;
  value: any;
}

const aggregationOptions: SelectOption[] = [
  { label: "Maximum", value: "max" },
  { label: "Average", value: "avg" },
  { label: "Sum", value: "sum" },
  { label: "Minimum", value: "min" },
];

export default function AddFileQueryFilterModal({
  isOpen,
  onClose,
  onAddFilter,
  projectId,
  editingFilter,
}: AddFileQueryFilterModalProps): JSX.Element {
  const isEditMode = !!editingFilter;

  const { control, handleSubmit, reset, watch } = useForm<FilterFormData>({
    defaultValues: {
      reportRelativePath: editingFilter?.reportRelativePath || "",
      selectedFields: editingFilter ? Object.keys(editingFilter.fields) : [],
    },
  });

  const selectedReportPath = useWatch({
    control,
    name: "reportRelativePath",
  });

  const selectedFields = useWatch({
    control,
    name: "selectedFields",
  });

  // Fetch report paths
  const { data: reportPaths = [], isLoading: isLoadingReportPaths } =
    useReportPaths(projectId);

  // Fetch report features when a report path is selected
  const { data: reportFeatures = [], isLoading: isLoadingReportFeatures } =
    useReportFeatures(projectId, selectedReportPath || null);

  // Convert report paths to SelectOption format
  const reportPathOptions: SelectOption[] = useMemo(() => {
    return reportPaths.map(path => ({
      label: path,
      value: path,
    }));
  }, [reportPaths]);

  // Convert report features to SelectOption format
  const fieldOptions: SelectOption[] = useMemo(() => {
    return reportFeatures.map(feature => ({
      label: feature,
      value: feature,
    }));
  }, [reportFeatures]);

  // Manage field aggregations
  const [fieldAggregations, setFieldAggregations] = useState<
    FieldAggregation[]
  >(() => {
    if (editingFilter) {
      return Object.entries(editingFilter.fields).map(
        ([fieldName, config]) => ({
          fieldName,
          aggregation: config.aggregation,
        })
      );
    }
    return [];
  });

  // Manage filter conditions
  const [filterConditions, setFilterConditions] = useState<FilterCondition[]>(
    () => {
      if (editingFilter?.filter) {
        const filterArray =
          editingFilter.filter.and || editingFilter.filter.or || [];
        return filterArray.map(condition => {
          const fieldName = Object.keys(condition)[0];
          const operator = Object.keys(condition[fieldName])[0];
          const value = condition[fieldName][operator];
          return {
            field: fieldName,
            operator,
            value,
          };
        });
      }
      return [];
    }
  );

  const [filterLogic, setFilterLogic] = useState<FilterLogic>(() => {
    if (editingFilter?.filter) {
      return editingFilter.filter.and ? "AND" : "OR";
    }
    return "AND";
  });

  // Update aggregations when selectedFields change
  useEffect(() => {
    if (isOpen) {
      const currentFieldNames = new Set(
        fieldAggregations.map(f => f.fieldName)
      );
      const newFieldNames = new Set(selectedFields || []);

      // Remove aggregations for fields that are no longer selected
      const filteredAggregations = fieldAggregations.filter(f =>
        newFieldNames.has(f.fieldName)
      );

      // Add aggregations for newly selected fields (default to "max")
      const newAggregations: FieldAggregation[] = Array.from(newFieldNames)
        .filter(fieldName => !currentFieldNames.has(fieldName))
        .map(fieldName => ({
          fieldName,
          aggregation: "max" as const,
        }));

      setFieldAggregations([...filteredAggregations, ...newAggregations]);
    }
  }, [selectedFields, isOpen]);

  // Reset form when editingFilter changes or modal opens/closes
  useEffect(() => {
    if (isOpen) {
      const initialFields = editingFilter
        ? Object.keys(editingFilter.fields)
        : [];
      const initialAggregations = editingFilter
        ? Object.entries(editingFilter.fields).map(([fieldName, config]) => ({
            fieldName,
            aggregation: config.aggregation,
          }))
        : [];

      const initialConditions: FilterCondition[] = editingFilter?.filter
        ? (editingFilter.filter.and || editingFilter.filter.or || []).map(
            condition => {
              const fieldName = Object.keys(condition)[0];
              const operator = Object.keys(condition[fieldName])[0];
              const value = condition[fieldName][operator];
              return {
                field: fieldName,
                operator,
                value,
              };
            }
          )
        : [];

      const initialLogic: FilterLogic = editingFilter?.filter
        ? editingFilter.filter.and
          ? "AND"
          : "OR"
        : "AND";

      reset({
        reportRelativePath: editingFilter?.reportRelativePath || "",
        selectedFields: initialFields,
      });
      setFieldAggregations(initialAggregations);
      setFilterConditions(initialConditions);
      setFilterLogic(initialLogic);
    }
  }, [isOpen, editingFilter, reset]);

  const handleAggregationChange = (
    fieldName: string,
    aggregation: "max" | "avg" | "sum" | "min"
  ): void => {
    setFieldAggregations(prev =>
      prev.map(f => (f.fieldName === fieldName ? { ...f, aggregation } : f))
    );
  };

  const handleRemoveField = (fieldName: string): void => {
    setFieldAggregations(prev => prev.filter(f => f.fieldName !== fieldName));
    const currentFields = watch("selectedFields") || [];
    reset({
      ...watch(),
      selectedFields: currentFields.filter(f => f !== fieldName),
    });
  };

  // Filter condition handlers
  const handleAddCondition = (): void => {
    setFilterConditions([
      ...filterConditions,
      { field: "", operator: "", value: "" },
    ]);
  };

  const handleRemoveCondition = (index: number): void => {
    setFilterConditions(filterConditions.filter((_, i) => i !== index));
  };

  const handleConditionFieldChange = (index: number, field: string): void => {
    // Defensive check: ensure field is not empty before updating
    if (!field || String(field).trim() === "") {
      return;
    }
    const updated = [...filterConditions];
    updated[index] = { ...updated[index], field: String(field).trim() };
    setFilterConditions(updated);
  };

  const handleConditionOperatorChange = (
    index: number,
    operator: string
  ): void => {
    const updated = [...filterConditions];
    updated[index] = { ...updated[index], operator };
    setFilterConditions(updated);
  };

  const handleConditionValueChange = (index: number, value: any): void => {
    const updated = [...filterConditions];
    updated[index] = { ...updated[index], value };
    setFilterConditions(updated);
  };

  const onSubmit = (data: FilterFormData): void => {
    if (data.reportRelativePath) {
      const fields: Record<
        string,
        { aggregation: "max" | "avg" | "sum" | "min" }
      > = {};
      fieldAggregations.forEach(({ fieldName, aggregation }) => {
        fields[fieldName] = { aggregation };
      });

      // Filter out conditions with empty field names before creating filter
      const validFilterConditions = filterConditions.filter(
        condition =>
          condition.field &&
          String(condition.field).trim() !== "" &&
          condition.operator &&
          String(condition.operator).trim() !== ""
      );

      const filter: FileQueryFilter["filter"] =
        validFilterConditions.length > 0
          ? {
              [filterLogic.toLowerCase()]: validFilterConditions.map(
                condition => ({
                  [String(condition.field).trim()]: {
                    [String(condition.operator).trim()]: condition.value,
                  },
                })
              ),
            }
          : undefined;

      onAddFilter({
        reportRelativePath: data.reportRelativePath,
        fields,
        ...(filter && { filter }),
      });
      reset();
      setFieldAggregations([]);
      setFilterConditions([]);
      setFilterLogic("AND");
      onClose();
    }
  };

  const isFormValid = (): boolean => {
    const formValues = watch();
    // Only require file selection - fields and filter group are optional
    return !!formValues.reportRelativePath;
  };

  return (
    <Modal
      open={isOpen}
      setOpen={onClose}
      title={isEditMode ? "Edit Report Query" : "Create Report Query"}
      showButtons={false}
      size={ModalSize.XLarge}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* File Selection */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-900">
            Select File
          </label>
          <p className="mb-2 text-sm text-gray-500">
            Files appear here after a pipeline run publishes report results to
            Sample Tracker for this project. Only pipelines configured to
            publish results to Sample Tracker will show up here.
          </p>
          <SelectComponent
            control={control}
            name="reportRelativePath"
            options={reportPathOptions}
            placeholder="Choose a file to query..."
            enableClear={false}
            isLoading={isLoadingReportPaths}
          />
        </div>

        {/* Field Selection */}
        {selectedReportPath && (
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-900">
              Select Fields to Return
            </label>
            <SelectComponent
              control={control}
              name="selectedFields"
              options={fieldOptions}
              placeholder="Select fields..."
              enableClear={false}
              isMulti={true}
              isLoading={isLoadingReportFeatures}
            />
            {selectedFields && selectedFields.length > 0 && (
              <div className="mt-2 text-sm text-gray-500">
                {selectedFields.length} field
                {selectedFields.length !== 1 ? "s" : ""} selected
              </div>
            )}
          </div>
        )}

        {/* Data Aggregation Section */}
        {fieldAggregations.length > 0 && (
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-900">
              Data Aggregation (for multiple runs/files)
            </label>
            <div className="flex flex-wrap gap-2">
              {fieldAggregations.map(({ fieldName, aggregation }) => {
                const selectedAggregationOption = aggregationOptions.find(
                  opt => opt.value === aggregation
                );
                return (
                  <div
                    key={fieldName}
                    className="inline-flex items-center gap-2 rounded-md bg-gray-100 px-3 py-1.5">
                    <span className="text-sm text-gray-700 whitespace-nowrap flex-shrink-0">
                      {fieldName}
                    </span>
                    <div className="flex-shrink-0 min-w-[100px]">
                      <SelectComponent
                        name="aggregationSelect"
                        value={selectedAggregationOption || null}
                        onChange={selected => {
                          if (selected && !Array.isArray(selected)) {
                            handleAggregationChange(
                              fieldName,
                              (selected as SelectOption).value as
                                | "max"
                                | "avg"
                                | "sum"
                                | "min"
                            );
                          }
                        }}
                        options={aggregationOptions}
                        enableClear={false}
                        isSearchable={false}
                        menuPlacement="bottom"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveField(fieldName)}
                      className="ml-1 rounded p-0.5 hover:bg-gray-200 flex-shrink-0">
                      <XMarkIcon className="h-4 w-4 text-gray-600" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Filter Group Section */}
        {selectedReportPath && (
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-900">
                Filter Group
              </label>
              <button
                type="button"
                onClick={handleAddCondition}
                className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700">
                <PlusIcon className="h-4 w-4" />
                Add Condition
              </button>
            </div>
            <div className="rounded-md py-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {filterConditions.length > 1 && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700">
                        Within group:
                      </span>
                      <select
                        value={filterLogic}
                        onChange={e =>
                          setFilterLogic(e.target.value as FilterLogic)
                        }
                        className="text-sm border border-gray-300 rounded-md px-3 py-1.5 min-w-[70px] bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-gray-300 appearance-none cursor-pointer">
                        <option value="AND">AND</option>
                        <option value="OR">OR</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
              {filterConditions.length === 0 ? (
                <div className="rounded-md border-2 border-dashed border-gray-200 bg-gray-50 p-6 text-center">
                  <p className="text-sm text-gray-500">
                    No conditions in this group
                  </p>
                  <button
                    type="button"
                    onClick={handleAddCondition}
                    className="mt-2 text-sm text-indigo-600 hover:text-indigo-700">
                    Add Condition
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {filterConditions.map((condition, index) => (
                    <FileQueryFilterCondition
                      key={index}
                      index={index}
                      field={condition.field}
                      operator={condition.operator}
                      value={condition.value}
                      onFieldChange={handleConditionFieldChange}
                      onOperatorChange={handleConditionOperatorChange}
                      onValueChange={handleConditionValueChange}
                      onRemove={handleRemoveCondition}
                      fieldOptions={fieldOptions}
                      showLogicSelector={index > 0}
                      logic={filterLogic}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid()}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
            {isEditMode ? "Update Query" : "Create Query"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
